import React, { useEffect, useState, useCallback } from "react";
import { CheckCircle2, Clock, Target } from "lucide-react";
import api, { apiError } from "../../api";
import { useLang } from "../../i18n";
import { useConfig } from "../../context/ConfigContext";
import { wsLabel } from "../../utils";
import MagicalCard from "../../components/MagicalCard";
import { Icon, EmptyState, SkeletonPanel } from "../../components/common";
import { ChallengeStatusChip, daysLeftText } from "../../components/WeeklyChallengeCard";
import { toast } from "sonner";

export default function ChallengesPage() {
  const { t, pick, lang } = useLang();
  const { config } = useConfig();
  const [challenges, setChallenges] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/challenges");
    setChallenges(data.challenges);
  }, []);

  useEffect(() => { load(); }, [load]);

  const complete = async (ch) => {
    setBusyId(ch.id);
    try {
      const { data } = await api.post(`/challenges/${ch.id}/complete`);
      toast.success(data.status === "awaiting" ? t("challenges.submitted") : t("challenges.completed_toast"));
      load();
    } catch (e) {
      toast.error(apiError(e, t));
    } finally { setBusyId(null); }
  };

  if (!challenges) return <SkeletonPanel lines={5} />;

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="challenges-page">
      <h2 className="text-2xl sm:text-3xl font-black text-center">{wsLabel(config?.ui_labels, "challenges", lang, t("challenges.title"))}</h2>
      {challenges.length === 0 ? (
        <MagicalCard hover={false}><EmptyState icon="Target" title={t("challenges.empty")} testId="challenges-empty" /></MagicalCard>
      ) : (
        challenges.map((ch) => (
          <MagicalCard key={ch.id} hover testId={`challenge-card-${ch.id}`} className="!pt-5">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#EF4D9A] to-[#FF63B5] flex items-center justify-center shrink-0 glow-pink">
                <Icon name={ch.icon || "Target"} className="w-7 h-7 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="font-black text-lg">{pick(ch, "title")}</h4>
                  <ChallengeStatusChip status={ch.status} />
                </div>
                <p className="text-sm text-[#D8D4F2] font-semibold mt-1">{pick(ch, "desc")}</p>
                <div className="flex items-center gap-4 mt-3 flex-wrap text-sm font-bold">
                  <span className="text-[#FFC83D] flex items-center gap-1"><Target className="w-4 h-4" /> +{ch.points}</span>
                  {daysLeftText(ch, t) && <span className="text-[#27C9EE] flex items-center gap-1"><Clock className="w-4 h-4" /> {daysLeftText(ch, t)}</span>}
                </div>
                {ch.status === "active" && (
                  <button data-testid={`complete-challenge-${ch.id}`} disabled={busyId === ch.id} onClick={() => complete(ch)}
                    className="btn-magic btn-gold mt-4 text-sm">
                    <CheckCircle2 className="w-4 h-4" /> {t("challenges.complete")}
                  </button>
                )}
              </div>
            </div>
          </MagicalCard>
        ))
      )}
    </div>
  );
}
